import { CellProps } from 'react-table'
import { formatDateToPtBr } from '_metronic/helpers'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { PlainHeader, sortableColumn, StackedCell, TableColumn } from 'app/components'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { DispositivoGestaoList, SituacaoDispositivo } from '../../../core/_models'
import { linhaApoio, rotuloMaleta, rotuloTipo } from '../../../core/rotulos'
import { dataVencida } from '../../../core/datas'
import { Selo, SituacaoSelo } from '../../../components/Selos'
import { DispositivosActionsCell } from './DispositivosActionsCell'

export const DispositivosColumns = (
  t: TFunction,
  opcoes: { mostrarRegime: boolean; situacoesVisiveis: readonly SituacaoDispositivo[] }
): ReadonlyArray<TableColumn<DispositivoGestaoList>> => [
  sortableColumn<DispositivoGestaoList>({
    id: 'tipo',
    useQueryRequest,
    fields: [{ field: 'tipo', label: t('pagina.dispositivos.col.dispositivo') }],
    Cell: ({ row }: CellProps<DispositivoGestaoList>) => (
      <StackedCell
        primary={rotuloTipo(t, row.original.tipo)}
        secondary={linhaApoio(row.original.fabricante, row.original.modelo)}
      />
    ),
  }),
  sortableColumn<DispositivoGestaoList>({
    id: 'serie',
    cellWidth: '170px',
    useQueryRequest,
    fields: [{ field: 'serie', label: t('pagina.dispositivos.col.serie_patrimonio') }],
    Cell: ({ row }: CellProps<DispositivoGestaoList>) => (
      <StackedCell tabular primary={row.original.serie} secondary={row.original.patrimonio ?? ''} />
    ),
  }),
  {
    Header: () => <PlainHeader>{t('pagina.dispositivos.col.empresa')}</PlainHeader>,
    id: 'empresa',
    hideOnMobile: true,
    Cell: ({ row }: CellProps<DispositivoGestaoList>) => (
      <StackedCell primary={row.original.empresa?.nome ?? ''} />
    ),
  },
  {
    Header: () => <PlainHeader>{t('pagina.dispositivos.col.maleta')}</PlainHeader>,
    id: 'maleta',
    cellWidth: '150px',
    hideOnMobile: true,
    Cell: ({ row }: CellProps<DispositivoGestaoList>) => (
      <StackedCell primary={rotuloMaleta(t, row.original.maletaAtual)} />
    ),
  },
  sortableColumn<DispositivoGestaoList>({
    id: 'situacao',
    cellWidth: '150px',
    useQueryRequest,
    fields: [{ field: 'situacao', label: t('pagina.dispositivos.col.situacao') }],
    Cell: ({ row }: CellProps<DispositivoGestaoList>) => (
      <SituacaoSelo situacao={row.original.situacao} situacoesVisiveis={opcoes.situacoesVisiveis} />
    ),
  }),
  ...(opcoes.mostrarRegime
    ? [
        {
          Header: () => <PlainHeader>{t('pagina.dispositivos.col.regime')}</PlainHeader>,
          id: 'regime',
          cellWidth: '110px',
          hideOnMobile: true,
          Cell: ({ row }: CellProps<DispositivoGestaoList>) =>
            row.original.regime === 'VENDIDO' ? (
              <Selo>{t('dispositivos.rotulos.regime.VENDIDO')}</Selo>
            ) : (
              <StackedCell primary='' />
            ),
        } as TableColumn<DispositivoGestaoList>,
      ]
    : []),
  {
    Header: () => <PlainHeader>{t('pagina.dispositivos.col.proxima_calibracao')}</PlainHeader>,
    id: 'dtProximaCalibracao',
    cellWidth: '150px',
    hideOnMobile: true,
    Cell: ({ row }: CellProps<DispositivoGestaoList>) => {
      const data = row.original.dtProximaCalibracao
      return (
        <StackedCell
          tabular
          primary={formatDateToPtBr(data)}
          {...(dataVencida(data) ? { secondary: t('pagina.dispositivos.calibracao_vencida') } : {})}
        />
      )
    },
  },
  {
    Header: () => <PlainHeader>{t('cols.actions')}</PlainHeader>,
    id: 'actions',
    cellWidth: '120px',
    align: 'end',
    Cell: ({ row }: CellProps<DispositivoGestaoList>) => (
      <DispositivosActionsCell dispositivo={row.original} />
    ),
  },
]
